import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PLAYLIST_GRAPHIC,
  PlaylistGraphicResponseType,
  PlaylistGraphicsResponseType,
  PlaylistGraphicStateType,
  PlaylistGraphicsType,
  PlaylistGraphicType,
} from './types';
import { updatePlaylistGraphics } from './utils';

const playlistGraphicInitialState: PlaylistGraphicStateType = {
  playlistGraphics: null,
  selectedPlaylistGraphicId: null,
  selectedPlaylistGraphic: null,
  loading: true,
  error: null,
};

export const playlistGraphicSlice = createSlice({
  name: PLAYLIST_GRAPHIC,
  initialState: playlistGraphicInitialState,
  reducers: {
    // CREATE
    requestCreatePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveCreatePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = false;
    },
    failedCreatePlaylistGraphic: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },
    // DELETE
    requestDeletePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveDeletePlaylistGraphic: (
      playlistGraphic,
      { payload: playlistGraphicId }: PayloadAction<string>
    ) => {
      if (playlistGraphicId === playlistGraphic.selectedPlaylistGraphic?.id) {
        playlistGraphic.selectedPlaylistGraphic = null;
      }
      playlistGraphic.loading = false;
    },
    failedDeletePlaylistGraphic: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },

    // DUPLICATE
    requestDuplicatePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveDuplicatePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = false;
    },
    failedDuplicatePlaylistGraphic: (
      playlistGraphic,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },

    // UPDATE
    requestUpdatePlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveUpdatePlaylistGraphic: (
      playlistGraphic,
      { payload }: PayloadAction<PlaylistGraphicType>
    ) => {
      playlistGraphic.loading = false;
      if (!playlistGraphic.playlistGraphics) return;

      // Insert updated playlistGraphic into playlistGraphics array
      playlistGraphic.playlistGraphics = playlistGraphic.playlistGraphics?.map((graphic) =>
        graphic.id === payload.id ? { ...graphic, ...payload } : graphic
      );

      //Insert updated playlistGraphic into selectedPlaylistGraphic
      if (playlistGraphic?.selectedPlaylistGraphic?.id === payload.id) {
        playlistGraphic.selectedPlaylistGraphic = {
          ...playlistGraphic.selectedPlaylistGraphic,
          ...payload,
        };
      }
    },
    failedUpdatePlaylistGraphic: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },
    receiveSetPlaylistGraphicCanvases: (
      playlistGraphic,
      {
        payload,
      }: PayloadAction<{
        playlistGraphicId: string;
        canvasOverride: PlaylistGraphicType['canvasOverride'];
      }>
    ) => {
      if (!playlistGraphic.playlistGraphics) return;
      playlistGraphic.playlistGraphics = playlistGraphic.playlistGraphics.map((graphic) =>
        graphic.id === payload.playlistGraphicId
          ? { ...graphic, canvasOverride: payload.canvasOverride }
          : graphic
      );
      if (playlistGraphic.selectedPlaylistGraphic?.id === payload.playlistGraphicId) {
        playlistGraphic.selectedPlaylistGraphic = {
          ...playlistGraphic.selectedPlaylistGraphic,
          canvasOverride: payload.canvasOverride,
        };
      }
    },
    // GET ALL
    requestGetPlaylistGraphics: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveGetPlaylistGraphics: (
      playlistGraphic,
      { payload }: PayloadAction<PlaylistGraphicsResponseType | null>
    ) => {
      playlistGraphic.loading = false;

      playlistGraphic.playlistGraphics = payload || null;

      if (payload) {
        playlistGraphic.selectedPlaylistGraphic =
          payload.find((graphic) => graphic.id === playlistGraphic.selectedPlaylistGraphic?.id) ||
          null;
      }
    },
    failedGetPlaylistGraphics: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },
    // SET SELECTED
    requestSetSelectedPlaylistGraphic: (playlistGraphic) => {
      playlistGraphic.error = null;
    },
    receiveSetSelectedPlaylistGraphic: (
      playlistGraphic,
      { payload }: PayloadAction<PlaylistGraphicResponseType | null>
    ) => {
      playlistGraphic.selectedPlaylistGraphic = payload;
    },
    failedSetSelectedPlaylistGraphic: (
      playlistGraphic,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistGraphic.error = error;
    },
    // REORDER
    requestReorderPlaylistGraphics: (playlistGraphic) => {
      playlistGraphic.error = null;
    },
    receiveReorderPlaylistGraphics: (
      playlistGraphic,
      { payload: playlistGraphicIds }: PayloadAction<string[]>
    ) => {
      if (!playlistGraphic.playlistGraphics) return;
      playlistGraphic.playlistGraphics.sort((a, b) => {
        const indexA = playlistGraphicIds.indexOf(a.id);
        const indexB = playlistGraphicIds.indexOf(b.id);
        if (indexA < indexB) {
          return -1;
        }
        if (indexA > indexB) {
          return 1;
        }
        return 0;
      });
    },
    failedReorderPlaylistGraphics: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.error = error;
    },
    // UPDATE STATUS
    requestUpdateStatusPlaylistGraphics: () => {},
    receiveUpdateStatusPlaylistGraphics: (
      playlistGraphic,
      { payload }: PayloadAction<PlaylistGraphicsResponseType>
    ) => {
      if (!playlistGraphic.playlistGraphics) return;

      const result = playlistGraphic.playlistGraphics?.reduce<PlaylistGraphicsType>(
        (acc, graphic) => {
          // Should exclude playlistgrahics that are not in current array
          if (!playlistGraphic.playlistGraphics?.find((g) => g.id === graphic.id)) return acc;

          const updatedGraphic = payload.find((g) => g.id === graphic.id);
          if (updatedGraphic) {
            acc.push({ ...graphic, status: updatedGraphic.status });
            return acc;
          }
          graphic.status = {
            ...graphic.status,
            live: false,
          };
          //Insert updated playlistGraphic into selectedPlaylistGraphic
          if (playlistGraphic?.selectedPlaylistGraphic?.id === graphic.id) {
            playlistGraphic.selectedPlaylistGraphic = graphic;
          }
          acc.push(graphic);
          return acc;
        },
        []
      );

      playlistGraphic.playlistGraphics = result;
    },
    failedUpdateStatusPlaylistGraphics: (
      playlistGraphic,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistGraphic.error = error;
    },
    // UPDATE MULTIPLE
    requestUpdatePlaylistGraphics: (playlistGraphic) => {
      playlistGraphic.loading = true;
    },
    receiveUpdatePlaylistGraphics: (
      playlistGraphic,
      { payload }: PayloadAction<PlaylistGraphicsResponseType>
    ) => {
      playlistGraphic.loading = false;
      playlistGraphic.playlistGraphics = updatePlaylistGraphics(
        payload,
        playlistGraphic.playlistGraphics
      );

      if (playlistGraphic.selectedPlaylistGraphic) {
        const selectedPlaylistGraphic = payload.find(
          (graphic) => graphic.id === playlistGraphic.selectedPlaylistGraphic?.id
        );
        if (selectedPlaylistGraphic) {
          playlistGraphic.selectedPlaylistGraphic = selectedPlaylistGraphic;
        }
      }
    },
    failedUpdatePlaylistGraphics: (playlistGraphic, { payload: error }: PayloadAction<string>) => {
      playlistGraphic.loading = false;
      playlistGraphic.error = error;
    },
  },
});

const actions = playlistGraphicSlice.actions;
const playlistGraphicReducer = playlistGraphicSlice.reducer;

export { actions, playlistGraphicReducer };
